import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast, ToastOptions } from "react-toastify";
import { SavedView } from "@app/network-portfolio-management/interfaces";
import { useFilterContext } from "@app/network-portfolio-management/contexts/FilterContext";
import { SaveAsViewModal } from "@app/network-portfolio-management/components/SaveAsViewModal";
import { DefaultViewBadge } from "@app/network-portfolio-management/components/DefaultViewBadge";
import { NPMQesViewBadge } from "@app/network-portfolio-management/components/NPMQesViewBadge";
import { Modal } from "@app/shared/Modals";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { Toggle } from "@app/shared/Toggle";
import { QESButton } from "@app/shared/qes-standards/Button";

const toastOptions: ToastOptions = { containerId: "z-index-9999" };

const REMOVAL_ANIMATION_MS = 200;

interface ManageViewsModalProps {
  open: boolean;
  onClose: () => void;
}

export const ManageViewsModal: FC<ManageViewsModalProps> = ({ open, onClose }) => {
  const { defaultViewId, deleteView, loading, replaceView, savedViews, setDefaultView } = useFilterContext();
  const [editingView, setEditingView] = useState<SavedView | null>(null);
  const [pendingDeleteView, setPendingDeleteView] = useState<SavedView | null>(null);
  const [removingIds, setRemovingIds] = useState<Set<number>>(new Set());
  const [settingDefaultId, setSettingDefaultId] = useState<number | null>(null);
  const pendingTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => () => {
    pendingTimers.current.forEach(clearTimeout);
    pendingTimers.current.clear();
  }, []);

  const handleEdit = useCallback((view: SavedView) => {
    setEditingView(view);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditingView(null);
  }, []);

  const handleEditSaved = useCallback((view: SavedView, replacedId?: number) => {
    if (replacedId === undefined) return;
    replaceView(replacedId, view);
    toast.success(`View "${view.name}" updated.`, toastOptions);
  }, [replaceView]);

  const handleDelete = useCallback((view: SavedView) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      next.add(view.npmFilteredViewId);
      return next;
    });
    const timer = setTimeout(async () => {
      pendingTimers.current.delete(timer);
      try {
        await deleteView(view.npmFilteredViewId);
        toast.success(`View "${view.name}" deleted.`, toastOptions);
      } catch {
        setRemovingIds((prev) => {
          const next = new Set(prev);
          next.delete(view.npmFilteredViewId);
          return next;
        });
        toast.error(`Failed to delete view "${view.name}". Please try again.`, toastOptions);
      }
    }, REMOVAL_ANIMATION_MS);
    pendingTimers.current.add(timer);
  }, [deleteView]);

  // Gates the actual removal behind the confirmation modal: the row's Delete button only
  // stages a pending view, and deletion runs from here once the user confirms.
  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteView) {
      handleDelete(pendingDeleteView);
    }
    setPendingDeleteView(null);
  }, [handleDelete, pendingDeleteView]);

  // Radio-button semantics: exactly one toggle is on at a time. Clicking the toggle
  // that is already on is a no-op (there must always be a default); clicking an off
  // toggle promotes that view, which implicitly demotes the previous default.
  const handleDefaultToggle = useCallback(async (view: SavedView, currentValue: boolean) => {
    if (currentValue) return;
    setSettingDefaultId(view.npmFilteredViewId);
    try {
      if (view.isQesView) {
        await setDefaultView(null);
      } else {
        await setDefaultView(view.npmFilteredViewId);
      }
    } catch {
      toast.error(`Failed to update default view. Please try again.`, toastOptions);
    } finally {
      setSettingDefaultId(null);
    }
  }, [setDefaultView]);

  const modalBody = useMemo(() => (
    <div className="qes-npm-mv-modal" data-testid="npm-mv-modal">
      {loading && (
        <div className="qes-npm-mv-loading" data-testid="npm-mv-loading">Loading...</div>
      )}
      <ul className="qes-npm-mv-list">
        {savedViews.map((view) => {
          const isRemoving = removingIds.has(view.npmFilteredViewId);
          const isCurrentDefault = view.isQesView
            ? defaultViewId === null
            : view.npmFilteredViewId === defaultViewId;
          const isSettingDefault = settingDefaultId !== null;
          return (
            <li
              key={view.npmFilteredViewId}
              className={`qes-npm-mv-item${isRemoving ? " qes-npm-mv-item--removing" : ""}`}
              data-testid={`npm-mv-item-${view.npmFilteredViewId}`}
            >
              <div className="qes-npm-mv-item__left">
                <div className="qes-npm-mv-item__name">
                  {view.name}
                  {view.isQesView && <NPMQesViewBadge />}
                  {isCurrentDefault && <DefaultViewBadge />}
                </div>
                {view.description && (
                  <div
                    className="qes-npm-mv-item__description"
                    data-testid={`npm-mv-description-${view.npmFilteredViewId}`}
                  >
                    {view.description}
                  </div>
                )}
              </div>
              <div className="qes-npm-mv-item__actions">
                <Toggle
                  active={isCurrentDefault}
                  disabled={isRemoving || isSettingDefault}
                  label="Default"
                  labelSide="left"
                  onChange={(current) => void handleDefaultToggle(view, current)}
                  testId={`npm-mv-set-default-${view.npmFilteredViewId}`}
                />
                {!view.isQesView && (
                  <QESButton
                    qesType="none"
                    qesTestId={`npm-mv-edit-${view.npmFilteredViewId}`}
                    className="qes-npm-mv-btn"
                    disabled={isRemoving}
                    onClick={() => handleEdit(view)}
                  >
                    <QesIcon iconName={QesIcons.PencilV1} isDecorative className="qes-npm-mv-btn__icon" />
                    Edit
                  </QESButton>
                )}
                {!view.isQesView && (
                  <QESButton
                    qesType="none"
                    qesTestId={`npm-mv-delete-${view.npmFilteredViewId}`}
                    className="qes-npm-mv-btn qes-npm-mv-btn--danger"
                    disabled={isRemoving}
                    onClick={() => setPendingDeleteView(view)}
                  >
                    <QesIcon iconName={QesIcons.TrashV1} isDecorative className="qes-npm-mv-btn__icon" />
                    Delete
                  </QESButton>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  ), [defaultViewId, handleDefaultToggle, handleEdit, loading, removingIds, savedViews, settingDefaultId]);

  return (
    <>
      <Modal
        isVisible={open}
        closeModal={onClose}
        hideBodyScroll
        content={{
          body: modalBody,
          cancelButtonText: "",
          confirmButtonAction: onClose,
          confirmButtonText: "Done",
          confirmButtonType: "primary",
          title: "Manage Saved Views",
        }}
      />
      <SaveAsViewModal
        // key remounts the modal each time the user picks a different row, so the
        // pre-populated name/description initializers reflect the new editingView.
        key={editingView?.npmFilteredViewId ?? "manage-edit"}
        open={editingView !== null}
        editingView={editingView ?? undefined}
        onClose={handleEditClose}
        onSaved={handleEditSaved}
      />
      <Modal
        isVisible={pendingDeleteView !== null}
        closeModal={() => setPendingDeleteView(null)}
        content={{
          body: (
            <span data-testid="npm-mv-delete-confirm">
              Are you sure you want to delete <strong>{pendingDeleteView?.name}</strong>? This action is
              permanent and cannot be undone.
            </span>
          ),
          cancelButtonText: "Cancel",
          confirmButtonAction: handleConfirmDelete,
          confirmButtonText: "Delete",
          confirmButtonType: "primary-danger",
          title: "Delete saved view?",
        }}
      />
    </>
  );
};
